import type { ComponentType } from "react";
import { useMemo } from "react";

import type { DropdownTab, IDropdownButtonProps, TabbedDropdownProps } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { DropdownSmartTabs } from "app/pages/sales-intelligence/common-components/dropdown/DropdownTabs/DropdownTabs";
import DropdownTabPanel from "app/pages/sales-intelligence/common-components/form-fields/DropdownTabPanel/DropdownTabPanel";
import type { IAppsSdkDescriptionItemDto } from "app/pages/sales-intelligence/domain/sdk/types/dto";
import { sdkContentTypeMap } from "app/pages/sales-intelligence/domain/sdk/types/filter-values";
import { getAllSdkCategories } from "app/pages/sales-intelligence/domain/sdk/utils/descriptions";
import type { AppsSdksSignalSelection } from "app/pages/sales-intelligence/domain/signals/types/apps-signal";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import useDropdownTabbedSearch from "app/pages/sales-intelligence/modules/technology/hook/useDropdownTabbedSearch";
import type { DropdownBaseListItem } from "app/pages/sales-intelligence/modules/technology/types/dropdown";
import useToggleState from "app/pages/sales-intelligence/sub-modules/advanced-search/hooks/useToggleState";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

import {
    DROPDOWN_HEIGHT,
    StyledFilterTabbedDropdown as StyledSdksDropdown,
    VERTICAL_OFFSET,
} from "../../shared/FilterTabbedDropdown/styles";
import type { SdksFilterItem } from "../hooks/useSdksFilterState";

const FILTER_AUTO_OPEN_DELAY = 250;
const SECONDARY_NAME_SEPARATOR = " | ";
const CATEGORIES_TAB_ID = "sdk-categories";
const SDK_NAMES_TAB_ID = "sdk-names";

export type SdksDDItem = SdksFilterItem & {
    readonly secondaryName?: string;
    readonly isSelected: boolean;
};

type Props = Pick<TabbedDropdownProps<SdksDDItem>, "isDisabled"> & {
    options: readonly IAppsSdkDescriptionItemDto[];
    selectedValue: Readonly<AppsSdksSignalSelection>;
    searchPlaceholder?: string;
    buttonComponent: ComponentType<Pick<IDropdownButtonProps, "isOpen">>;
    onSelect(item: Readonly<SdksFilterItem>): void;
    onItemsSort?<I extends DropdownBaseListItem<string>>(items: ReadonlyArray<I>): ReadonlyArray<I>;
    onNoResultsFound?(): void;
};

const FilterSdksDropdown = (props: Props) => {
    const {
        options,
        selectedValue,
        isDisabled,
        searchPlaceholder,
        onSelect,
        onItemsSort,
        onNoResultsFound,
        buttonComponent: ButtonComponent,
    } = props;

    const [isOpen, setIsOpen] = useToggleState(false);

    /* Tab names must carry a literal "(<count>)": the search hook rewrites that substring with the filtered count. */
    const tabs = useMemo((): readonly DropdownTab<SdksDDItem>[] => {
        const selectedCategories = selectedValue.sdkCategories ?? [];
        const selectedSdks = selectedValue.sdks ?? [];
        const categories = getAllSdkCategories(options);

        return [
            {
                id: CATEGORIES_TAB_ID,
                name: translate(signalsI18nKeys.apps.sdksFilter.tabs.categories, {
                    amountOfItems: categories.length,
                }),
                items: categories.map((category) => ({
                    name: category,
                    type: sdkContentTypeMap.category,
                    isSelected: selectedCategories.includes(category),
                })),
            },
            {
                id: SDK_NAMES_TAB_ID,
                name: translate(signalsI18nKeys.apps.sdksFilter.tabs.sdkNames, {
                    amountOfItems: options.length,
                }),
                items: options.map(({ name, categories: sdkCategories }) => ({
                    name,
                    type: sdkContentTypeMap.sdk,
                    secondaryName: sdkCategories.join(SECONDARY_NAME_SEPARATOR),
                    isSelected: selectedSdks.includes(name),
                })),
            },
        ];
    }, [options, selectedValue]);

    const { tabsWithSortedItems, searchTerm, searchProps } = useDropdownTabbedSearch<SdksDDItem>(
        {
            tabs,
            onItemsSort,
            onNoResultsFound,
            searchPlaceholder,
        },
        { shouldProcessItems: isOpen },
    );

    useDelayedAction(FILTER_AUTO_OPEN_DELAY, () => setIsOpen(true), !isDisabled);

    const handleItemClick = (item: SdksDDItem): void => {
        const { name, type } = item;

        onSelect({ name, type });
    };

    const renderButton = (buttonProps: Pick<TabbedDropdownProps<SdksDDItem>, "isOpen">) => (
        <ButtonComponent
            isOpen={isOpen}
            {...buttonProps}
        />
    );

    return (
        <StyledSdksDropdown
            isDisabled={isDisabled}
            buttonComponent={renderButton}
            tabs={tabsWithSortedItems}
            isOpen={isOpen}
            onToggle={setIsOpen}
            searchProps={searchProps}
            searchTerm={searchTerm}
            onItemClick={handleItemClick}
            panelComponent={DropdownTabPanel}
            tabsView={DropdownSmartTabs}
            verticalOffset={VERTICAL_OFFSET}
            height={DROPDOWN_HEIGHT}
        />
    );
};

export default FilterSdksDropdown;
